import { Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, HStack, QuestionOutlineIcon, Spacer, VStack } from 'native-base';
import Octicons from 'react-native-vector-icons/Octicons';
import { filter, map, some } from 'lodash';

import { Button, CleanIcon, Modal, Text } from '@pimm/base';
import { PositionTypeEnum } from '@pimm/services/lib/sms-workforce';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { useClickToDrop, usePositioningPlan } from '../context';
import { PositioningSlot } from '../reducers';
import { PositioningSelectEmployee } from './positioning-select-employee';
import { PositioningSelectPosition } from './positioning-select-position';
import DroppablePositionSlot from './droppable-position-slot';
import { PopoverSelectOpsLeader } from './popover-select-ops-leader';
import { PopoverTooltipPositionActions } from './popover-tooltip-position-actions';
import { ConfirmQuickAssignCleaning } from './confirm-quick-assign-cleaning';

type PositioningPlanServicesProps = {
  emptyMessage?: React.ReactNode;
  isDisabled?: boolean;
};

export const PositioningPlanServices = ({ emptyMessage, isDisabled }: PositioningPlanServicesProps) => {
  const { t } = useTranslation();
  const { dayBlock, positioning, deleteAssignee, replaceAssignee, updatePositionSlot, moveTo } = usePositioningPlan();
  const { dropItem, setIsDisabled: setIsClickToDropDisabled } = useClickToDrop();
  const modalSelect = useModalFocus<PositioningSlot>();
  const modalPosition = useModalFocus<{ isPrimary?: boolean; positionSlotId: string }>();
  const modalQuickAssign = useModalFocus();

  const isConfirmed = !!positioning?.confirmedTime;
  const isQuickAssignEnabled =
    positioning?.positionSlots?.filter(_ => _.positionType !== PositionTypeEnum.NonService).every(_ => !!_.assignee?.employeeId) ?? false;

  useEffect(() => {
    let hasPending = positioning?.positionSlots?.some(_ => !_.id && _.positionType === PositionTypeEnum.NonService) ?? false;

    if (!hasPending) {
      const availableEmployees = filter(
        positioning?.employees,
        _ => !some<PositioningSlot>(positioning?.positionSlots, slot => slot.assignee?.employeeId === _.employeeId),
      ).map(employee => ({ assignee: employee }));

      hasPending = availableEmployees.some(_ => _.assignee?.positionJobId);
    }
    setIsClickToDropDisabled(!positioning?.confirmedTime || hasPending);
  }, [positioning]);

  return (
    <Fragment>
      <VStack minH="full">
        {/* Title Header */}
        <HStack space={1.5} alignItems="center" px={3} w="full" h={12}>
          {/* <PlusCircleIcon size="18px" color={colors.gray[600]} /> */}
          <HStack space={2} alignItems="flex-start">
            <Text size="xl" fontWeight={700} color="black" lineHeight="xs" alignSelf="center">
              {t('positioning_plan:service_positions')}
            </Text>

            <PopoverTooltipPositionActions
              crossOffset={-120}
              placement="right top"
              trigger={triggerProps => (
                <Button variant="unstyled" shadow={1} px={0} minH={8} minW={8} bg="white" {...triggerProps}>
                  <QuestionOutlineIcon size={5} color="gray.900" />
                </Button>
              )}
            />
          </HStack>

          <Spacer />

          {isConfirmed && !dropItem && (
            <HStack space={1.5} alignItems="center">
              <Box
                rounded="full"
                alignItems="center"
                justifyContent="center"
                w={5}
                h={5}
                borderWidth={1}
                borderColor="white"
                bg="warning.300"
              >
                <Octicons name="star" size={14} color="black" />
              </Box>

              <Text size="md" fontWeight={600} color="black">
                {t('positioning_plan:ops_leader')}
              </Text>

              <PopoverSelectOpsLeader isDisabled={isDisabled} positionSlots={positioning?.positionSlots} onChange={updatePositionSlot} />

              <Button
                py={0}
                pl={1.5}
                minH={8}
                shadow={0}
                borderColor="blue.500"
                bg="blue.500"
                isDisabled={isDisabled || !isQuickAssignEnabled}
                _text={{ color: 'white' }}
                _hover={{ borderColor: 'blue.600', bg: 'blue.600' }}
                _pressed={{ borderColor: 'blue.600', bg: 'blue.600' }}
                startIcon={<CleanIcon size="18px" color="white" />}
                onPress={() => modalQuickAssign.setOpen()}
              >
                {t('positioning_plan:quick_assign')}
              </Button>
            </HStack>
          )}
        </HStack>

        {isConfirmed ? (
          <HStack flexWrap="wrap" px={1} pb={2}>
            {/* Position Slots */}
            {map(positioning?.positionSlots, (position, index) => {
              if (position.positionType === PositionTypeEnum.NonService) return undefined;

              return (
                <Box key={`position-slot.${index}.${position.id}`} p={1.5} w="1/3">
                  <DroppablePositionSlot
                    key={`droppable-position-slot.${[
                      position.id,
                      position.positionId,
                      position.positionType,
                      isDisabled ? 'disabled' : 'enabled',
                      positioning?.subBlockTime,
                      position.assignee?.employeeId,
                    ]
                      .filter(Boolean)
                      .join('.')}`}
                    dayBlock={positioning?.dayBlock}
                    isDisabled={isDisabled}
                    position={position}
                    subBlockTime={positioning?.subBlockTime}
                    onChange={updatePositionSlot}
                    onDeleteAssignee={deleteAssignee}
                    onMoveTo={moveTo}
                    onEditPosition={isPrimary => modalPosition.setOpen({ isPrimary: isPrimary, positionSlotId: position.id! })}
                    onReplaceAssignee={replaceAssignee}
                    onSelect={() => modalSelect.setOpen(position)}
                  />
                </Box>
              );
            })}
          </HStack>
        ) : (
          <Box flex={1} alignItems="center" justifyContent="center">
            {emptyMessage}
          </Box>
        )}
      </VStack>

      <Modal
        size="md"
        _content={{ rounded: 'xl', p: 5, pt: 4, maxW: 420 }}
        isOpen={isQuickAssignEnabled && modalQuickAssign.isOpen}
        onClose={modalQuickAssign.setHide}
        hideClose
      >
        <ConfirmQuickAssignCleaning
          dayBlock={dayBlock}
          subBlockTime={positioning?.subBlockTime}
          siteId={positioning?.siteId}
          onClose={() => modalQuickAssign.setHide()}
        />
      </Modal>

      <Modal isOpen={modalSelect.isOpen} noPadding size="md" _content={{ maxWidth: 340 }} hideClose>
        {!!modalSelect.payload && (
          <PositioningSelectEmployee
            isDisabled={isDisabled}
            position={modalSelect.payload}
            onCancel={modalSelect.setHide}
            onSave={updatePositionSlot}
          />
        )}
      </Modal>

      <Modal
        isOpen={modalPosition.isOpen}
        noPadding
        size="xl"
        hideClose
        _content={{ maxWidth: 980, h: '80%' }}
        onClose={modalPosition.setHide}
      >
        {!!modalPosition.payload && (
          <PositioningSelectPosition
            positionSlots={positioning?.positionSlots}
            {...modalPosition.payload}
            onCancel={modalPosition.setHide}
            onSave={updatePositionSlot}
          />
        )}
      </Modal>
    </Fragment>
  );
};
